import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import { getGuide, GUIDE_QUERY_KEY, setGuideHidden } from '../lib/guide-api.js';

/**
 * Krok PRU1 bod 3 — PRUVODCE JDE SKRYT A VRATIT (Nastavení / menu profilu).
 *
 * Jeden prepinac, dve mista (stejny vzor jako `appearance.tsx`): stav je
 * per makler na serveru (`PATCH /api/me/guide`), oba vstupy ctou a meni
 * tentyz dotaz `GUIDE_QUERY_KEY`, takze se nerozejdou s kartou na Dnesku.
 *
 * „Zobrazit" je videt i u splneneho pruvodce, ktery uz sam zmizel —
 * kdo si ho vrati, uvidi znovu „hotovo".
 */
function useGuideToggle(): {
  readonly shown: boolean | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly toggle: () => void;
} {
  const t = useT();
  const queryClient = useQueryClient();
  const guide = useQuery({ queryKey: GUIDE_QUERY_KEY, queryFn: getGuide });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const shown = guide.data === undefined ? null : guide.data.visible;
  const toggle = (): void => {
    if (shown === null) return;
    setBusy(true);
    setError(null);
    void setGuideHidden(shown)
      .then((next) => queryClient.setQueryData(GUIDE_QUERY_KEY, next))
      .catch((e: unknown) => setError(t('webAdmin.guide.actionError', { error: errorMessage(e) })))
      .finally(() => setBusy(false));
  };
  return { shown, busy, error, toggle };
}

/** Karta v Nastavení. */
export function GuideSettingsCard(): React.JSX.Element {
  const t = useT();
  const { shown, busy, error, toggle } = useGuideToggle();
  return (
    <div
      className="rh-card"
      style={{ maxWidth: 640, padding: 16, marginBottom: 16 }}
      data-testid="nastaveni-guide-card"
    >
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.guide.settingsTitle')}</h2>
      <p className="mb-2 text-sm text-gray-600">{t('webAdmin.guide.settingsHint')}</p>
      <button
        type="button"
        className="rh-v4-btn rh-v4-btn--tinted"
        data-testid="nastaveni-guide-toggle"
        data-shown={shown === null ? undefined : String(shown)}
        disabled={busy || shown === null}
        onClick={toggle}
      >
        {shown === true ? t('webAdmin.guide.settingsHide') : t('webAdmin.guide.settingsShow')}
      </button>
      {error !== null ? (
        <p role="alert" className="rh-a2-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Radek v menu profilu (vykresluje se jen pri otevrenem menu). */
export function GuideMenuRow({
  className,
}: {
  readonly className: string;
}): React.JSX.Element | null {
  const t = useT();
  const { shown, busy, toggle } = useGuideToggle();
  if (shown === null) return null;
  return (
    // Menu po prepnuti zustane otevrene — clovek vidi, ze se popisek zmenil.
    <DropdownMenu.Item asChild onSelect={(event) => event.preventDefault()}>
      <button
        type="button"
        className={className}
        data-testid="avatar-menu-guide-toggle"
        data-shown={String(shown)}
        disabled={busy}
        onClick={toggle}
      >
        <span className="rh-a2-ico" aria-hidden="true">
          <Sparkles size={18} strokeWidth={1.75} />
        </span>
        {shown ? t('webAdmin.guide.menuHide') : t('webAdmin.guide.menuShow')}
      </button>
    </DropdownMenu.Item>
  );
}
